import { useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { FORMULA_GROUPS, TAGGED_FORMULAS } from '../lib/formulas.ts';
import type { TaggedFormula } from '../lib/formulas.ts';

/**
 * 登录页全屏背景：「数学星空」。
 *
 * 每条公式是一个独立的生命体：随机的大小、随机角度、随机时机出现——
 * 显影、停留、淡去，然后是下一条。永不排排坐。
 *
 * 防重叠摆放：新公式先渲染量出实际尺寸（含旋转后的包围盒），再做
 * 拒绝采样——只落在不与任何在屏公式、登录卡、右下角铭文相交的空位，
 * 且彼此留有间隙。找不到空位就这一拍跳过，等旧公式淡出腾地方；
 * 大定理放不下则稍候重试。密度随屏幕大小自适应。
 *
 * 景深：大而清晰的离观者近，小而模糊的远——二维画面里有三维层次。
 * 随机间隔调度（非固定节拍），各自处在生命周期的不同阶段。
 *
 * 尊重 prefers-reduced-motion：静态撒一把不动画（同样防重叠）。
 */

const MAX_ALIVE = 20; // 同屏公式上限（实际数量由防重叠摆放自适应）
const SPAWN_MIN_MS = 260; // 随机调度间隔
const SPAWN_MAX_MS = 760;
const SIZE_MIN = 13; // 字号范围（偶尔出现大定理）
const SIZE_MAX = 46;
const GAP = 16; // 公式之间的最小间隙（px），含视差微移的余量
const TAG_PAD = 18; // 学科标签悬在公式上方的占位（px）
const MAX_ATTEMPTS = 30; // 单条公式的找位尝试次数

/**
 * 学科星团锚点：每个学科一个天区（环绕登录卡分布），公式优先落在
 * 自己学科的团内——知识的版图有地理结构，不再是均匀噪声。
 */
const CLUSTER_ANCHORS: readonly [number, number][] = [
  [9, 10], [30, 5], [55, 8], [80, 12],
  [95, 28], [93, 62], [82, 90], [56, 94],
  [28, 93], [5, 86], [3, 52], [14, 32],
  [97, 10], [50, 97], [3, 32],
];

const clusterOf = (field: string): [number, number] => {
  const gi = FORMULA_GROUPS.findIndex((g) => g.field === field);
  const anchor = CLUSTER_ANCHORS[(gi === -1 ? 0 : gi) % CLUSTER_ANCHORS.length];
  return anchor ?? [10, 10];
};

/** 轴对齐包围盒（px） */
interface Rect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

const inflate = (r: Rect, m: number): Rect => ({
  x1: r.x1 - m,
  y1: r.y1 - m,
  x2: r.x2 + m,
  y2: r.y2 + m,
});

const overlapsAny = (r: Rect, obstacles: readonly Rect[]): boolean =>
  obstacles.some((o) => r.x1 < o.x2 && r.x2 > o.x1 && r.y1 < o.y2 && r.y2 > o.y1);

/** 字号：幂律偏小，偶尔一条大定理镇场 */
function pickSize(): number {
  const r = Math.random();
  if (r > 0.93) return SIZE_MAX * (0.85 + Math.random() * 0.15);
  return SIZE_MIN + Math.pow(Math.random(), 1.6) * (SIZE_MAX - SIZE_MIN) * 0.6;
}

/** 显影风格：随机三选一（模糊淡入 / 打字机揭示 / 放大浮出） */
function pickStyle(): string {
  const styles = ['style-blur', 'style-clip', 'style-zoom'];
  return styles[Math.floor(Math.random() * styles.length)] ?? 'style-blur';
}

export function LoginArt() {
  const layerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // 在屏公式的占位矩形表：防重叠的唯一事实来源
    const occupied = new Map<HTMLElement, Rect>();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let theoremTimer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const pickFormula = (): TaggedFormula | undefined =>
      TAGGED_FORMULAS[Math.floor(Math.random() * TAGGED_FORMULAS.length)];

    const buildFormulaEl = (
      tf: TaggedFormula,
      className: string,
      fontSize: number,
      rotDeg: number,
      opacity: string,
      depth: string,
    ): HTMLElement => {
      const el = document.createElement('span');
      el.className = className;
      el.innerHTML = katex.renderToString(tf.tex, { throwOnError: false });
      // 学科标签：公式的出身
      const tag = document.createElement('i');
      tag.className = 'formula-tag';
      tag.textContent = tf.field;
      el.appendChild(tag);
      el.style.fontSize = `${fontSize.toFixed(1)}px`;
      el.style.setProperty('--rot', `${rotDeg.toFixed(1)}deg`);
      el.style.setProperty('--op', opacity);
      el.dataset.depth = depth;
      el.style.visibility = 'hidden'; // 找到空位前不可见
      return el;
    };

    /**
     * 给已渲染（隐藏）的公式找空位：量出旋转后的包围盒，在学科团内
     * 做拒绝采样，先贴团心再放宽，最后全屏兜底；命中即定位并返回
     * 占位矩形，找不到返回 null（调用方负责移除元素）。
     */
    const placeEl = (el: HTMLElement, field: string, clearance: number): Rect | null => {
      const lw = layer.clientWidth;
      const lh = layer.clientHeight;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (lw < 120 || lh < 120) return null;

      const rot = ((parseFloat(el.style.getPropertyValue('--rot')) || 0) * Math.PI) / 180;
      const cos = Math.abs(Math.cos(rot));
      const sin = Math.abs(Math.sin(rot));
      const rw = w * cos + h * sin;
      const rh = w * sin + h * cos;

      // 障碍物：在屏公式 + 登录卡 + 右下角铭文
      const obstacles: Rect[] = [...occupied.values()];
      const card = document.querySelector('.auth-card');
      if (card) {
        const cr = card.getBoundingClientRect();
        obstacles.push(inflate({ x1: cr.left, y1: cr.top, x2: cr.right, y2: cr.bottom }, 24));
      } else {
        // 兜底：屏幕中央视作登录卡区
        obstacles.push({ x1: lw * 0.3, y1: lh * 0.3, x2: lw * 0.7, y2: lh * 0.7 });
      }
      const caption = layer.parentElement?.querySelector('.art-caption');
      if (caption) {
        const pr = caption.getBoundingClientRect();
        obstacles.push(inflate({ x1: pr.left, y1: pr.top, x2: pr.right, y2: pr.bottom }, 10));
      }

      const [ax, ay] = clusterOf(field);
      const edge = 6; // 距屏幕边缘的最小距离

      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        let fx: number;
        let fy: number;
        if (i < 16) {
          const spread = i < 10 ? 1 : 2.2; // 先贴团心，再放宽到团周
          fx = ax + (Math.random() - 0.5) * 14 * spread;
          fy = ay + (Math.random() - 0.5) * 11 * spread;
        } else {
          fx = 2 + Math.random() * 96; // 最后全屏兜底
          fy = 2 + Math.random() * 96;
        }
        const cx = (fx / 100) * lw;
        const cy = (fy / 100) * lh;
        const rect: Rect = {
          x1: cx - rw / 2 - GAP - clearance,
          y1: cy - rh / 2 - GAP - clearance - TAG_PAD,
          x2: cx + rw / 2 + GAP + clearance,
          y2: cy + rh / 2 + GAP + clearance,
        };
        if (rect.x1 < edge || rect.y1 < edge + TAG_PAD) continue;
        if (rect.x2 > lw - edge || rect.y2 > lh - edge) continue;
        if (overlapsAny(rect, obstacles)) continue;
        el.style.left = `${(cx - w / 2).toFixed(1)}px`;
        el.style.top = `${(cy - h / 2).toFixed(1)}px`;
        el.style.visibility = '';
        return rect;
      }
      return null;
    };

    // 强制 reflow 后加 on：让显影 transition 生效
    const reveal = (el: HTMLElement): void => {
      void el.offsetWidth;
      el.classList.add('on');
    };

    const retire = (el: HTMLElement, delay: number): void => {
      window.setTimeout(() => {
        el.classList.add('out'); // 淡出
        window.setTimeout(() => {
          el.remove();
          occupied.delete(el);
        }, 1300);
      }, delay);
    };

    const spawnOne = (): void => {
      const tf = pickFormula();
      if (!tf) return;
      const fontSize = pickSize();
      const depth = Math.min(1, (fontSize - SIZE_MIN) / (SIZE_MAX - SIZE_MIN));
      const el = buildFormulaEl(
        tf,
        `formula-f ${pickStyle()}`,
        fontSize,
        Math.random() * 26 - 13,
        (0.42 + depth * 0.5).toFixed(2),
        depth.toFixed(2),
      );
      layer.appendChild(el);
      const rect = placeEl(el, tf.field, 0);
      if (!rect) {
        el.remove(); // 屏上已挤不下，这一拍空过，等旧公式淡出腾地方
        return;
      }
      occupied.set(el, rect);

      // 知识关联弧线：低概率与一条已有公式连线（同屏弧线最多 3 条）
      if (Math.random() < 0.22 && svg.childElementCount < 3 && occupied.size >= 2) {
        window.setTimeout(() => drawLink(el), 900);
      }

      reveal(el);
      retire(el, 2200 + Math.random() * 4200);
    };

    // 定理时刻：每 14~26s 一条超大字号公式慢速显影（reduced 模式无）
    const spawnTheorem = (): boolean => {
      const tf = pickFormula();
      if (!tf) return true;
      const el = buildFormulaEl(
        tf,
        'formula-f style-zoom spotlight',
        54 + Math.random() * 16,
        Math.random() * 16 - 8,
        '0.95',
        '0.95',
      );
      layer.appendChild(el);
      const rect = placeEl(el, tf.field, 14); // 大定理要更宽裕的四周
      if (!rect) {
        el.remove();
        return false;
      }
      occupied.set(el, rect);
      reveal(el);
      // 聚光灯：大定理出场时，全屏其余公式退暗 0.6s——宇宙安静一秒
      const art = layer.parentElement;
      if (art) {
        art.classList.add('dim');
        window.setTimeout(() => art.classList.remove('dim'), 5200);
      }
      window.setTimeout(() => el.classList.add('out'), 8200);
      window.setTimeout(() => {
        el.remove();
        occupied.delete(el);
      }, 9600);
      return true;
    };

    const scheduleTheorem = (delay = 14000 + Math.random() * 12000): void => {
      if (stopped) return;
      theoremTimer = setTimeout(() => {
        if (stopped) return;
        if (!spawnTheorem()) {
          scheduleTheorem(3000); // 大定理放不下，稍等重试
          return;
        }
        scheduleTheorem();
      }, delay);
    };

    const spawn = (): void => {
      if (stopped) return;
      if (occupied.size < MAX_ALIVE) spawnOne();
      // 随机间隔调度下一条（非固定节拍）；放不下的一拍自然空过
      if (!reduced) {
        timer = setTimeout(spawn, SPAWN_MIN_MS + Math.random() * (SPAWN_MAX_MS - SPAWN_MIN_MS));
      }
    };

    // 鼠标视差：近大远小，各深度层反向微移（三维星空）
    let mouseX = 0.5;
    let mouseY = 0.5;
    let parallaxRaf = 0;
    let parallaxPending = false;
    const applyParallax = (): void => {
      parallaxPending = false;
      const dx = (mouseX - 0.5) * 34;
      const dy = (mouseY - 0.5) * 24;
      for (const el of occupied.keys()) {
        const depth = parseFloat(el.dataset.depth ?? '0.5');
        el.style.setProperty('--px', `${(-dx * depth).toFixed(1)}px`);
        el.style.setProperty('--py', `${(-dy * depth).toFixed(1)}px`);
      }
    };
    const onMouseMove = (e: MouseEvent): void => {
      mouseX = e.clientX / window.innerWidth;
      mouseY = e.clientY / window.innerHeight;
      if (!parallaxPending) {
        parallaxPending = true;
        parallaxRaf = requestAnimationFrame(applyParallax);
      }
    };
    window.addEventListener('mousemove', onMouseMove);

    // 知识关联弧线：新显影的公式与屏上一条随机旧公式之间连一条流动的细弧线
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.classList.add('formula-links');
    layer.parentElement?.appendChild(svg);

    const centerOf = (el: HTMLElement): [number, number] => {
      const r = el.getBoundingClientRect();
      return [r.left + r.width / 2, r.top + r.height / 2];
    };

    const drawLink = (el: HTMLElement): void => {
      if (stopped || !el.isConnected) return;
      const others = [...occupied.keys()].filter((e) => e !== el && e.isConnected);
      if (others.length === 0) return;
      const target = others[Math.floor(Math.random() * others.length)];
      if (!target) return;
      const [x1, y1] = centerOf(el);
      const [x2, y2] = centerOf(target);
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      const mx = (x1 + x2) / 2 + (Math.random() - 0.5) * 160;
      const my = (y1 + y2) / 2 + (Math.random() - 0.5) * 120;
      path.setAttribute('d', `M ${x1} ${y1} Q ${mx} ${my} ${x2} ${y2}`);
      svg.appendChild(path);
      window.setTimeout(() => path.classList.add('gone'), 4200);
      window.setTimeout(() => path.remove(), 5400);
    };

    // 先量后摆必须建立在真实字体上：KaTeX 字体换装会改变公式实测尺寸。
    // 隐形探针（含大型算符）触发字体请求，fonts.ready 等待加载完成，
    // 2s 兜底防止极端情况拖住开场。
    const probe = document.createElement('span');
    probe.className = 'formula-f';
    probe.style.visibility = 'hidden';
    probe.innerHTML = katex.renderToString('\\sum _{i=1}^{n} \\int _{0}^{\\infty } f(x)\\,dx', {
      throwOnError: false,
    });
    layer.appendChild(probe);

    const startShow = (): void => {
      probe.remove();
      if (stopped) return;
      if (reduced) {
        // 静态撒一把：同样防重叠，放不下就少几条
        let placed = 0;
        for (let i = 0; i < 14 && placed < 8; i++) {
          const tf = pickFormula();
          if (!tf) continue;
          const fontSize = pickSize();
          const depth = Math.min(1, (fontSize - SIZE_MIN) / (SIZE_MAX - SIZE_MIN));
          const el = buildFormulaEl(
            tf,
            'formula-f on',
            fontSize,
            Math.random() * 26 - 13,
            (0.42 + depth * 0.5).toFixed(2),
            depth.toFixed(2),
          );
          layer.appendChild(el);
          const rect = placeEl(el, tf.field, 0);
          if (!rect) {
            el.remove();
            continue;
          }
          occupied.set(el, rect);
          placed++;
        }
      } else {
        // 首屏先撒几条（不用等节拍），错开显影时间
        for (let i = 0; i < 4; i++) setTimeout(spawn, 150 + i * 380);
        timer = setTimeout(spawn, 1300);
        scheduleTheorem(9000);
      }
    };
    Promise.race([document.fonts.ready, new Promise((r) => setTimeout(r, 2000))]).then(startShow);

    return () => {
      stopped = true;
      if (timer !== null) clearTimeout(timer);
      if (theoremTimer !== null) clearTimeout(theoremTimer);
      cancelAnimationFrame(parallaxRaf);
      window.removeEventListener('mousemove', onMouseMove);
      svg.remove();
      layer.replaceChildren();
      occupied.clear();
    };
  }, []);

  return (
    <div className="auth-art" aria-hidden="true">
      <div ref={layerRef} className="formula-layer" />
      <div className="art-caption">{TAGGED_FORMULAS.length} formulas · from the last four centuries</div>
    </div>
  );
}
